import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatInput } from '@angular/material';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { concat } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'fc-search-input',
  templateUrl: './fc-search-input.component.html',
  styleUrls: ['./fc-search-input.component.scss']
})
export class FcSearchInputComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public control: FormControl;

  @Input()
  public isLocationInput = false;

  @Input()
  public label: string;

  @Input()
  public showSearchIcon = false;

  @Input()
  public showChevronIcon = false;

  @Input()
  public requiredMark = false;

  @Input()
  public preloadOptions = false;

  @Input()
  public searchOnFocus = false;

  @Input()
  public legacyStyle = false;

  @Input()
  public validation = [];

  @Input()
  public isDuplicatedForm = false;

  @Input()
  public sortOptionsFn: (items: any[]) => any[];

  @Input()
  public translateErrorPrefix: string;

  @Input()
  public search: (searchTerm: string, limit?: number, offset?: number) => Promise<any[]>;

  @Input()
  public displayWith: (value: any) => string;

  @Input()
  public enableInfiniteScroll = false;

  @ViewChild('input', { static: true })
  private inputElement: ElementRef<MatInput>;

  @ViewChild('matAutocomplete', { static: true })
  private matAutocomplete: MatAutocomplete;

  @Input()
  public options = [];

  @Input()
  public displayText: string;

  @Input()
  public showAllOption: boolean;

  @Input()
  public reload$: Subject<void>;

  @Input()
  public showAllOptionText: string;

  public loading = false;
  public optionsListOpened = false;
  public googleCustomerAddressErrors = [];
  public googleCustomerAddressWarnings = [];
  public hasMoreOptions = true;
  public optionsOffset = 0;
  public readonly searchTerm = new FormControl(null);

  private scrollHandler: Function;
  private readonly removeSubscriptions = new Subject<void>();
  private readonly optionsLimit = 10;

  constructor(fuseTranslationLoaderService: FuseTranslationLoaderService) {
    fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.validation && changes.validation.currentValue) {
      this.googleCustomerAddressErrors = changes.validation.currentValue.errors;
      this.googleCustomerAddressWarnings = changes.validation.currentValue.warnings;
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.control.value && this.control.value.isAllSelected) {
      this.control.setValue({ name: this.showAllOptionText, isAllSelected: true });
    }

    if (this.reload$) {
      this.reload$.subscribe(() => {
        this.options = [];
        this.searchTerm.updateValueAndValidity({ emitEvent: true });
      });
    }

    this.searchTerm.valueChanges
      .pipe(
        takeUntil(this.removeSubscriptions),
        debounceTime(500),
        filter((value) => typeof value === 'string'),
        map((value) => value.trim().toLowerCase()),
        tap(() => this.loading = true),
        switchMap(() => this.search(this.searchTerm.value, this.optionsLimit, 0)),
        tap(() => this.loading = false)
      )
      .subscribe((options) => {
        this.resetQueryParams();

        if (!options) {
          return;
        }

        this.hasMoreOptions = options.length >= this.optionsLimit;

        const filteredOptions = (this.sortOptionsFn && this.sortOptionsFn(options)) || options;

        if (!this.searchTerm.value && this.showAllOption) {
          const allOption = { name: this.showAllOptionText, isAllSelected: true };
          this.options = concat([allOption], filteredOptions);
          return;

        }

        this.options = filteredOptions;
      });

    this.control.valueChanges
      .pipe(takeUntil(this.removeSubscriptions))
      .subscribe((value) => {
        this.displayText = this.displayWith(value) || '';

        if (value && this.isLocationInput) {
          this.displayText = value.name;
        }

        if (!value) {
          this.searchTerm.setValue(null);
        }
      });

    this.control.statusChanges
      .pipe(takeUntil(this.removeSubscriptions))
      .subscribe(() => {
        if (this.control.disabled) {
          this.searchTerm.disable();
        } else {
          this.searchTerm.enable();
          this.setPreLoadedItems();
        }
      });

    this.displayText = this.displayText ? this.displayText : this.displayWith(this.control.value) || '';

    if (this.control.disabled) {
      this.searchTerm.disable();
    } else {
      this.searchTerm.enable();
      this.setPreLoadedItems();
    }
  }

  public async onFocus(): Promise<void> {
    if (this.searchOnFocus) {
      this.searchTerm.setValue(this.searchTerm.value || '');
    }
  }

  public resetSearch(): void {
    this.searchTerm.setValue('');
  }

  public resetQueryParams(): void {
    this.optionsOffset = 0;
    this.hasMoreOptions = true;
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event && event.option && event.option.value;
    this.control.setValue(value || undefined);
  }

  public onOptionCleared(): void {
    this.control.setValue(undefined);
    this.searchTerm.setValue('');
    setTimeout(() => this.inputElement.nativeElement.focus());
  }

  onAutocompleteScroll(): void {
    this.optionsListOpened = true;
    if (this.options.length) {
      this.loading = false;
    }

    setTimeout(() => {
      if (
        this.matAutocomplete &&
        this.matAutocomplete.panel &&
        this.matAutocomplete.panel.nativeElement
      ) {
        this.matAutocomplete.panel.nativeElement.scrollTo({
          top: 0,
        });

        this.scrollHandler = (event: any): void => {
          if (this.hasMoreOptions && this.enableInfiniteScroll) {
            const {
              scrollTop,
              scrollHeight,
              clientHeight
            } = event.target;

            const marginOfError = 1;

            if ((scrollTop + clientHeight) >= (scrollHeight - marginOfError)) {
              this.loading = true;
              this.optionsOffset += this.optionsLimit;
              this.loadMoreOptions();
            }
          }
        };

        this.matAutocomplete.panel.nativeElement.addEventListener(
          'scroll',
          this.scrollHandler
        );
      }
    });
  }

  loadMoreOptions(): void {
    this.search(this.searchTerm.value, this.optionsLimit, this.optionsOffset).then(((options) => {
      this.options.push(...options);
      this.loading = false;
      this.hasMoreOptions = options.length >= this.optionsLimit;
    }));
  }


  private setPreLoadedItems(): void {
    if (this.preloadOptions && !this.options.length) {
      this.searchTerm.setValue('');
    }
  }
}
